import { Button } from '@mui/material'
import styled from '@emotion/styled/macro'
import type { ThemeVariant } from '../primary/ThemeButton'
import theme from 'theme'

export const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'themevariant' && prop !== 'noActive'
})<{
  themevariant?: ThemeVariant
  noActive?: boolean | undefined
}>`
  border: 1px solid transparent;
  border-radius: ${theme.attributes.borderRadius.tertiary};
  background: ${theme.colors.cta};
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 13px ${theme.spacing.medium}px;
  gap: ${theme.spacing.small}px;
  font-family: ${theme.typography.fontFamily.primary};
  font-weight: ${theme.typography.fontWeight.bold};
  font-size: ${theme.typography.fontSize.large};
  line-height: 22px;
  text-align: center;
  color: ${theme.colors.surfaceContainerBackground};
  height: 48px;
  text-transform: none;
  white-space: nowrap;

  &:hover {
    background: ${theme.colors.buttonHollowHover};
    color: ${theme.colors.surfaceContainerBackground};
  }

  &:active,
  &:focus,
  &.Mui-focusVisible,
  &.Mui-focused {
    background: ${theme.colors.buttonHollowFocus};
    color: ${theme.colors.cta};
  }

  &:disabled,
  &.Mui-disabled {
    background: ${theme.colors.buttonHollowDisabled};
    color: ${theme.colors.surfaceContainerBackground};
  }

  ${({ themevariant }) =>
    themevariant &&
    themevariant === 'secondary' &&
    `
      color: ${theme.colors.btnSecondaryText};
      background: transparent;
      border-color: ${theme.colors.btnSecondaryBorder};

      &:hover {
        border-color: ${theme.colors.btnSecondaryHoverBg};
        background: ${theme.colors.btnSecondaryHoverBg};
        color: ${theme.colors.menuItemText};
      }

      &:active,
      &:focus,
      &.Mui-focusVisible,
      &.Mui-focused {
        background: transparent;
        color: ${theme.colors.cta};
        border-color: ${theme.colors.cta};
      }

      &:disabled,
      &.Mui-disabled {
        border-color: ${theme.colors.btnSecondaryDisabledBorder};
        background: ${theme.colors.btnSecondaryDisabledBg};
        color: ${theme.colors.btnSecondaryDisabledText};
      }

      html[data-theme='light'] & {
        &:hover {
          border-color: ${theme.colors.cardFillTertiary};
          background: ${theme.colors.cardFillTertiary};
          color: ${theme.colors.menuItemText};
        }
      }
  `};

  ${({ themevariant }) =>
    themevariant &&
    themevariant === 'tertiary' &&
    `
      background: ${theme.colors.btnTertiaryBg};
      color: ${theme.colors.cta};
      
      &:hover {
        background: ${theme.colors.cta};
        color: ${theme.colors.surfaceContainerBackground};
      }
      
      &:active,
      &:focus,
      &.Mui-focusVisible,
      &.Mui-focused {
        background: ${theme.colors.btnTertiaryBg};
        color: ${theme.colors.cta};
        border-color: ${theme.colors.cta};
      }
      
      &:disabled,
      &.Mui-disabled {
        border-color: transparent;
        background: ${theme.colors.borderMedium};
        color: ${theme.colors.btnSecondaryDisabledText};
      }

      html[data-theme='light'] & {
        &:disabled,
        &.Mui-disabled {
          background: ${theme.colors.cardFillTertiary};
        }
      }
  `};

  ${({ themevariant }) =>
    themevariant &&
    themevariant === 'ghost' &&
    `
      background: transparent !important;
      padding: 0;
      outline: 0;
      border: 0;
      color: ${theme.colors.cta};
      font-family: ${theme.typography.fontFamily.primary};
      font-size: ${theme.typography.fontSize.large};
      font-style: normal;
      font-weight: ${theme.typography.fontWeight.extraThick};
      line-height: 22px;
      width: fit-content !important;
      height: fit-content !important;

      &:hover {
        color: ${theme.colors.buttonHollowHover};
      }

      &:active,
      &:focus,
      &.Mui-focusVisible,
      &.Mui-focused {
        color: ${theme.colors.btnGhostFocus};
      }

      &:disabled,
      &.Mui-disabled {
        color: ${theme.colors.btnSecondaryDisabledText};
      }
  `};

  ${({ themevariant }) =>
    themevariant &&
    themevariant === 'hollow' &&
    `
      background: ${theme.colors.cta};
      color: ${theme.colors.surfaceContainerBackground};

      &:hover {
        background: ${theme.colors.buttonHollowHover};
        color: ${theme.colors.surfaceContainerBackground};
      }

      &:active,
      &:focus,
      &.Mui-focusVisible,
      &.Mui-focused {
        background: ${theme.colors.buttonHollowFocus};
        color: ${theme.colors.cta};
      }

      &:disabled,
      &.Mui-disabled {
        background: ${theme.colors.buttonHollowDisabled};
        color: ${theme.colors.surfaceContainerBackground};
      }
  `};
`
